/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import history from "../../../../@history";
import UserModel from "../model/UserModel";
import { showMessage } from "../../../store/BASE/messageSlice";

export const getUser = createAsyncThunk(
  "users/getUser",
  async (id, { dispatch, getState }) => {
    try {
      // send request to get user
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/users/${id}`
      );
      // get response data
      const data = await response.data;
      // return data
      return data;
    } catch (error) {
      // if error, redirect to users page
      history.push({ pathname: `/users` });
      // show error message
      dispatch(showMessage({ message: "User Not Found", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const addUser = createAsyncThunk(
  "users/addUser",
  async (user, { dispatch, getState }) => {
    try {
      // set user id
      user.user_id = getState().user.id;
      // send request to add user
      const response = await axios.post(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/users`,
        user
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "User Added" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "User Not Added", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async (user, { dispatch, getState }) => {
    try {
      // set user id
      user.user_id = getState().user.id;
      // send request to update user
      const response = await axios.patch(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/users/${user.id}`,
        user
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "User Updated" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "User Not Updated", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const removeUser = createAsyncThunk(
  "users/removeUser",
  async (id, { dispatch, getState }) => {
    try {
      // send request to remove user
      const response = await axios.delete(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/users/${id}`
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "User Removed" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "User Not Removed", variant: "error" }));
      // return null
      return null;
    }
  }
);

// define selector
export const selectUser = ({ users }) => users.instance;

// define slice
const userSlice = createSlice({
  name: "user",
  initialState: null,
  reducers: {
    newUser: (state, action) => UserModel(),
    resetUser: () => null,
  },
  extraReducers: {
    [getUser.pending]: (state, action) => null,
    [getUser.fulfilled]: (state, action) => action.payload,
    [updateUser.fulfilled]: (state, action) => action.payload,
    [removeUser.fulfilled]: (state, action) => null,
  },
});

// export actions
export const { resetUser, newUser } = userSlice.actions;

export default userSlice.reducer;
